import { CompareTableInterface } from '@adapters/bigcommerce/types/compareTable';
import { CompareTableSlice } from '@adapters/prismic/types/brandedStorePage';
import { FranchiseCompareConfig } from '_components/Product/FranchiseCompareTable/types';
import { ProductCompareConfig } from '_components/Product/ProductCompareTable/types';
import { buildFranchiseCompareConfig } from '@utils/comparison/franchise/buildFranchiseCompareConfig';
import { buildProductCompareConfig } from '@utils/comparison/buildProductCompareTableProps';

export type CompareTableSliceData =
  | { kind: 'franchise'; config: FranchiseCompareConfig }
  | { kind: 'product'; config: ProductCompareConfig };

type SharedConfig = Pick<
  FranchiseCompareConfig,
  'heading' | 'contextLink' | 'productTypeLabel'
>;

/**
 * Lays the slice's authored copy over the Akeneo table's. A field merch left
 * blank keeps the table's value; the context link is replaced only as a
 * label-and-url pair, since one without the other is not a link.
 */
const applyOverrides = (
  primary: CompareTableSlice['primary'] | undefined,
  base: SharedConfig
): SharedConfig => {
  const line1 = primary?.heading_line_1?.trim() || base.heading?.line1;
  const line2 = primary?.heading_line_2?.trim();
  const linkLabel = primary?.context_link_label?.trim();
  const linkUrl = primary?.context_link?.trim();
  const contextLink =
    linkLabel && linkUrl
      ? { label: linkLabel, url: linkUrl }
      : base.contextLink;

  return {
    heading: { line1, line2 },
    // On a category page the link is the next step of the shopping flow.
    ...(contextLink
      ? { contextLink: { ...contextLink, openInNewTab: false } }
      : {}),
  };
};

export type CompareTablesById = Record<string, CompareTableInterface>;

/** The resolved Akeneo table a compare_table slice names, if it resolved. */
export const selectCompareTable = (
  primary: CompareTableSlice['primary'] | undefined,
  compareTables: CompareTablesById | undefined
): CompareTableInterface | undefined =>
  compareTables?.[primary?.compare_table_id?.trim() ?? ''];

/**
 * ED-226: the compare_table slice resolved against the Akeneo table it names.
 * Null when the table did not resolve or has no columns, so the slice renders
 * nothing.
 */
const mapCompareTableData = (
  primary: CompareTableSlice['primary'] | undefined,
  table: CompareTableInterface | undefined,
  locale: string
): CompareTableSliceData | null => {
  if (!table?.items?.length) return null;
  if (table.comparisonType !== 'franchise' && table.items.length < 2) {
    return null;
  }

  if (table.comparisonType === 'franchise') {
    const base = buildFranchiseCompareConfig(table, locale);
    return {
      kind: 'franchise',
      config: { ...base, ...applyOverrides(primary, base) },
    };
  }

  const base = buildProductCompareConfig(table, locale);
  return {
    kind: 'product',
    config: { ...base, ...applyOverrides(primary, base) },
  };
};

export default mapCompareTableData;
